import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Staffel ("Bespaar met bundels") price block.
 *
 * The tier prices are read from the `mnml.volume_pricing_tiers` metafield server-side in
 * Liquid (blocks/bbd-tier-price.liquid → snippets/bbd-tier-price-table.liquid). The variant
 * JSON exposed to JS carries no metafields, so on variant change we do not recompute prices
 * client-side: we take the freshly re-rendered section HTML the theme hands us via the
 * `shopify:product:select` event and swap in this block's counterpart, matched by block id.
 *
 * This block lives outside product-information's own `.shopify-section`, so it can't rely on
 * that ancestor to receive the event — it listens on `document` instead, like
 * assets/quick-add.js, and relies on the product-card/data-product-id guards below to ignore
 * variant switches from unrelated product forms elsewhere on the page.
 *
 * @extends {Component}
 */
class BbdTierPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Re-render the tier table for the newly selected variant.
   * @param {Event & { promise?: Promise<any>, target: EventTarget }} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const next = html.querySelector(`bbd-tier-price[data-block-id="${this.dataset.blockId}"]`);
        // The host element is always rendered now, so presence alone is not enough — decide by
        // whether the re-rendered variant actually produced a tier table.
        const hasTable = next?.querySelector('.bbd-tier-price__table') != null;

        if (hasTable) {
          this.replaceChildren(...next.cloneNode(true).childNodes);
          this.classList.remove('bbd-tier-price--hidden');
        } else {
          // The newly selected variant has no tiers → hide and clear.
          this.replaceChildren();
          this.classList.add('bbd-tier-price--hidden');
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[bbd-tier-price] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('bbd-tier-price')) {
  customElements.define('bbd-tier-price', BbdTierPrice);
}
